import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { useShallow } from 'zustand/react/shallow'
import { populateWeek, unpopulateWeek } from '../pages/generator/generatorUtils/populates.js'
import { ROUTE_WEEK_VIEW } from '../router/Routes.jsx'
import { toggleRealEnabledService } from '../services/DaysService.js'
import {
  buildWeekDraft,
  confirmSaveWithoutLocalDraft,
  deleteWeekDraft,
  putWeekDraft,
  WEEK_DRAFT_SCOPE_GENERATOR,
  WEEK_DRAFT_SCOPE_INDISPO,
} from '../services/WeekDraftService.js'
import {
  getWeekForMGService,
  getWeekService,
  importNewUsersService,
  saveIndispoService,
  saveWeekService,
  syncStockmanagerTrainingsService,
  toggleCanAskIndispoService,
  toggleIsWeekEditableService,
  validateWorkedHoursService,
  toggleVisibleMGService,
} from '../services/WeeksService'
import { setHasUnsavedWeekDraft } from '../services/UnsavedDraftGuard.js'
import { SESSION_DEFAULT_URATION } from '../utils/constantes.js'
import dayjs from '../utils/dayjsHelper.js'
import { buildWorkedHoursSkippedDetails } from '../utils/planningWorkedHours.js'
import { deepCloneButKeepId } from '../utils/utils'
import {
  buildDayWorkedHoursByUser,
  getWorkedHoursUserId,
  roundWorkedHours,
} from '../utils/workedHours.js'
import { useDateStore } from './DateStore.jsx'
import { useDrawerStore } from './DrawerStore'
import { useRoomsStore } from './RoomsStore'
import { useSelectionStore } from './SelectionStore'
import { useUsersStore } from './UsersStore'
import { useDebounce } from '../utils/customHooks.js'
import { useAuthentificationStore } from './AuthenticationStore.jsx'
import { useSessionStore } from './SessionStore.jsx'

const MAX_UNDO_SIZE = 40
const VALIDATION_STYLE_ID = 'planningValidation'

const toDayKey = (date) => dayjs(date).format('YYYY-MM-DD')

const buildValidationCommentText = (initials) => {
  const now = dayjs()
  const formattedDate = now.format('DD/MM/YY')
  const formattedTime = now.format('HH:mm')
  return `OK ${initials} (${formattedDate} ${formattedTime})`
}

const setValidationCommentOnDay = (day, text) => {
  const places = [1, 2]
  places.forEach((placeId) => {
    const existing = day.commentaries.find(
      (c) => c.place_id === placeId && c.styleId === VALIDATION_STYLE_ID
    )
    if (existing) {
      existing.text = text
      existing.styleId = VALIDATION_STYLE_ID
      return
    }
    day.commentaries.push({
      place_id: placeId,
      text,
      styleId: VALIDATION_STYLE_ID,
      timeStamp: `${new Date().getTime()}-${placeId}`,
    })
  })
}

const buildWorkedHoursPayload = (week, dayDate) => {
  const targetDay = week.days.find((d) => d.isReal && toDayKey(d.date) === toDayKey(dayDate))
  if (!targetDay) {
    return []
  }

  const byUser = buildDayWorkedHoursByUser(targetDay, {
    isReal: true,
    includeAdditionalTimeDetails: true,
  })

  return Array.from(byUser.values()).map((entry) => {
    const user = week.users_week.find((uw) => getWorkedHoursUserId(uw?.user) === entry.userId)
      ?.user
    return {
      ...entry,
      initials: user?.initials || entry.initials || '??',
      login: user?.pseudo || entry.login || '',
      hoursWorked: roundWorkedHours(entry.hoursWorked),
    }
  })
}

const addWorkedHoursSkippedInfo = (dayKey, skippedCount, skippedDetails) => {
  const { addData, setShowDrawer } = useDrawerStore.getState()
  const texts = [
    {
      text: `${skippedCount} ligne(s) envoyée(s) par Planning n'ont pas été importée(s) côté Mon Espace.`,
    },
    ...skippedDetails.map((detail) => ({ text: detail })),
  ]

  addData({
    alert: {
      severity: 'warning',
      texts,
    },
    text: `Heures Mon Espace non importées - ${dayKey}`,
  })
  setShowDrawer(true)
}

const recordForUndo = (state, weekToSave) => {
  state.weekSaves.push(weekToSave)
  state.weekSaves.length > MAX_UNDO_SIZE && state.weekSaves.shift()
}

const PROTECTED_SAVE_CONFIG = {
  skipAuthRedirect: true,
  suppressAuthErrorToast: true,
}

const getCurrentRoute = () => {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

const writeWeekDraft = async (week, route, scope) => {
  const userId = useAuthentificationStore.getState().user?._id
  const draft = buildWeekDraft({ userId, route, scope, week })
  if (!draft) {
    return {
      draft: null,
      isDraftSaved: false,
    }
  }

  try {
    await putWeekDraft(draft)
    setHasUnsavedWeekDraft(true)
    return {
      draft,
      isDraftSaved: true,
    }
  } catch (error) {
    console.warn('[WeekDraft] Unable to save draft before server save', error)
    setHasUnsavedWeekDraft(true)
    return {
      draft,
      isDraftSaved: false,
    }
  }
}

const removeWeekDraft = async (draftKey) => {
  if (!draftKey) {
    setHasUnsavedWeekDraft(false)
    return
  }

  try {
    await deleteWeekDraft(draftKey)
    setHasUnsavedWeekDraft(false)
  } catch (error) {
    console.warn('[WeekDraft] Unable to delete saved draft', error)
  }
}

const protectServerSave = async ({
  week,
  route,
  scope,
  buildRetry,
  allowWithoutLocalDraft = false,
}) => {
  const { draft, isDraftSaved } = await writeWeekDraft(week, route, scope)
  if (!draft) {
    toast.error('Brouillon local impossible à préparer. Sauvegarde serveur bloquée.')
    return {
      canSave: false,
      draft,
      retryAfterReauth: buildRetry(),
      weekPayload: null,
    }
  }

  let retryAfterReauth = buildRetry()
  if (!isDraftSaved) {
    if (!allowWithoutLocalDraft) {
      const shouldSaveWithoutLocalDraft = await confirmSaveWithoutLocalDraft()
      if (!shouldSaveWithoutLocalDraft) {
        return {
          canSave: false,
          draft: null,
          retryAfterReauth,
          weekPayload: null,
        }
      }
    }
    retryAfterReauth = buildRetry({ allowWithoutLocalDraft: true })
  }

  const result = await useSessionStore.getState().ensureFreshSessionForSave({
    draftKey: isDraftSaved ? draft.key : null,
    retry: retryAfterReauth,
  })
  if (!result.ok) {
    return {
      canSave: false,
      draft: isDraftSaved ? draft : null,
      retryAfterReauth,
      weekPayload: null,
    }
  }

  return {
    canSave: true,
    draft: isDraftSaved ? draft : null,
    retryAfterReauth,
    weekPayload: draft.week,
  }
}

const markSaveSessionExpired = ({ draftKey, retry, result }) => {
  useSessionStore.getState().markExpired({
    draftKey,
    retry,
    message: result?.data?.message || 'Session expirée',
  })
}

export const useWeekStore = create(
  immer((set, get) => ({
    isWeekLoading: false,
    isWeekSaving: false,
    isDirty: false,
    isDayValidationLoading: false,
    week: null,
    weekSaves: [],

    // Fetch the week of the current state.date
    fetchWeek: async () => {
      const users = useUsersStore.getState().users
      const rooms = useRoomsStore.getState().rooms
      if (Object.keys(users).length === 0) return
      if (Object.keys(rooms).length === 0) return
      const date = useDateStore.getState().date
      const isWeekLoading = get().isWeekLoading
      if (isWeekLoading) return
      set({ isWeekLoading: true })
      const result = await getWeekService(date.toISOString())
      set({ isWeekLoading: false })
      if (result?.status === 200) {
        const nextWeek = result?.data
        if (nextWeek) {
          populateWeek(nextWeek, users, rooms)
          set({ week: nextWeek, isDirty: false, weekSaves: [] })
        }
      } else {
        set({ week: null })
      }
    },

    // Fetch the week for the MG View
    fetchWeekForMG: async () => {
      const users = useUsersStore.getState().users
      const rooms = useRoomsStore.getState().rooms
      if (Object.keys(users).length === 0) return
      if (Object.keys(rooms).length === 0) return
      const date = useDateStore.getState().date
      const isWeekLoading = get().isWeekLoading
      if (isWeekLoading) return
      set({ isWeekLoading: true })
      const result = await getWeekForMGService(date.toISOString())
      set({ isWeekLoading: false })
      if (result?.status === 200) {
        const nextWeek = result?.data
        if (nextWeek) {
          populateWeek(nextWeek, users, rooms)
          set({ week: nextWeek, weekSaves: [] })
        }
      } else {
        set({ week: null })
      }
    },

    importNewUsers: async () => {
      const fetchWeek = get().fetchWeek
      set({ isWeekSaving: true })
      const week = get().week
      const result = await importNewUsersService(week._id, toDayKey(week.date))
      set({ isWeekSaving: false })
      if (result?.status === 200) {
        toast.success(result?.data?.message)
      }
      if (result?.status === 200 || result?.data?.severity === 'info') {
        await useUsersStore.getState().fetchUsers(true)
        await fetchWeek()
      }
    },

    syncTrainings: async () => {
      const fetchWeek = get().fetchWeek
      set({ isWeekSaving: true })
      const result = await syncStockmanagerTrainingsService()
      set({ isWeekSaving: false })
      if (result?.status === 200) {
        toast.success(result?.data?.message)
        await useUsersStore.getState().fetchUsers(true)
        await fetchWeek()
      }
    },

    // Save the week
    saveWeek: async (options = {}) => {
      const week = get().week
      if (!week) return false

      const route = options?.route || getCurrentRoute()
      const buildRetry = ({ allowWithoutLocalDraft = options?.allowWithoutLocalDraft } = {}) => {
        return () => get().saveWeek({ route, allowWithoutLocalDraft })
      }
      const { canSave, draft, retryAfterReauth, weekPayload } = await protectServerSave({
        week,
        route,
        scope: WEEK_DRAFT_SCOPE_GENERATOR,
        buildRetry,
        allowWithoutLocalDraft: options?.allowWithoutLocalDraft,
      })
      if (!canSave) return false

      const users = useUsersStore.getState().users
      const rooms = useRoomsStore.getState().rooms
      set({ isWeekSaving: true })
      const result = await saveWeekService(week._id, weekPayload, PROTECTED_SAVE_CONFIG).finally(
        () => set({ isWeekSaving: false })
      )
      if (result?.status === 401) {
        markSaveSessionExpired({ draftKey: draft?.key, retry: retryAfterReauth, result })
        return false
      }
      if (result?.status === 200) {
        toast.success(result?.data?.message)
        const updatedWeek = result?.data?.updatedWeek
        populateWeek(updatedWeek, users, rooms)
        set({ week: updatedWeek, isDirty: false, weekSaves: [] })
        await removeWeekDraft(draft?.key)
        return true
      }
      return false
    },

    validateDayPair: async (dayDate, options = {}) => {
      const returnValidationResult = (validationResult) =>
        options?.returnResult ? validationResult : validationResult.ok
      const finishValidation = (validationResult) => {
        if (!options?.keepValidationLoading) {
          set({ isDayValidationLoading: false })
        }
        return returnValidationResult(validationResult)
      }
      const failValidation = (message, { shouldStopWeekValidation = false } = {}) => {
        if (!options?.suppressToasts && message) {
          toast.error(message)
        }
        return finishValidation({
          ok: false,
          dayKey: toDayKey(dayDate),
          message,
          shouldStopWeekValidation,
        })
      }

      const week = get().week
      if (!week) {
        return failValidation('Aucune semaine à valider', { shouldStopWeekValidation: true })
      }

      const user = useAuthentificationStore.getState().user
      const initials = user?.initials || '??'
      const text = buildValidationCommentText(initials)
      const dayKey = toDayKey(dayDate)
      const route = options?.route || getCurrentRoute()
      const buildRetry = ({ allowWithoutLocalDraft = options?.allowWithoutLocalDraft } = {}) => {
        return () => get().validateDayPair(dayDate, { route, allowWithoutLocalDraft })
      }

      if (!options?.keepValidationLoading) {
        set({ isDayValidationLoading: true })
      }
      const { canSave, draft, retryAfterReauth, weekPayload } = await protectServerSave({
        week,
        route,
        scope: WEEK_DRAFT_SCOPE_GENERATOR,
        buildRetry,
        allowWithoutLocalDraft: options?.allowWithoutLocalDraft,
      })
      if (!canSave) {
        return failValidation(null, { shouldStopWeekValidation: true })
      }

      const users = useUsersStore.getState().users
      const rooms = useRoomsStore.getState().rooms
      const currentSaveResult = await saveWeekService(
        week._id,
        weekPayload,
        PROTECTED_SAVE_CONFIG
      )
      if (currentSaveResult?.status === 401) {
        markSaveSessionExpired({
          draftKey: draft?.key,
          retry: retryAfterReauth,
          result: currentSaveResult,
        })
        return finishValidation({
          ok: false,
          dayKey,
          message: currentSaveResult?.data?.message || 'Session expirée',
          shouldStopWeekValidation: true,
        })
      }
      if (currentSaveResult?.status !== 200) {
        return failValidation(
          currentSaveResult?.data?.message ||
            "Échec de sauvegarde avant l'envoi des heures à Mon Espace"
        )
      }

      const savedWeek = currentSaveResult?.data?.updatedWeek
      if (!savedWeek) {
        return failValidation(
          "Échec de récupération de la semaine sauvegardée avant l'envoi Mon Espace"
        )
      }

      populateWeek(savedWeek, users, rooms)
      set({ week: savedWeek, isDirty: false, weekSaves: [] })
      await removeWeekDraft(draft?.key)

      const hoursByUser = buildWorkedHoursPayload(savedWeek, dayKey)
      const result = await validateWorkedHoursService(dayKey, hoursByUser)
      if (result?.status !== 200) {
        return failValidation(result?.data?.message || 'Échec de validation de la journée')
      }

      const nextWeek = JSON.parse(JSON.stringify(savedWeek))

      nextWeek.days
        .filter((d) => d.isReal && toDayKey(d.date) === toDayKey(dayDate))
        .forEach((d) => {
          setValidationCommentOnDay(d, text)
        })

      unpopulateWeek(nextWeek)
      const saveResult = await saveWeekService(savedWeek._id, nextWeek)

      if (saveResult?.status === 200) {
        const skippedCount = result?.data?.externalData?.skipped?.length || 0
        const skippedDetailsFromResponse = Array.isArray(result?.data?.skippedDetails)
          ? result.data.skippedDetails
          : []
        const skippedDetails =
          skippedDetailsFromResponse.length > 0
            ? skippedDetailsFromResponse
            : buildWorkedHoursSkippedDetails(result?.data?.externalData?.skipped)
        if (skippedCount > 0) {
          addWorkedHoursSkippedInfo(dayKey, skippedCount, skippedDetails)
          !options?.suppressToasts && toast.warn(
            `Validation enregistrée avec ${skippedCount} ligne(s) non importée(s). Détail dans Infos.`
          )
        } else {
          !options?.suppressToasts && toast.success('Validation de journée enregistrée')
        }
        const updatedWeek = saveResult?.data?.updatedWeek
        populateWeek(updatedWeek, users, rooms)
        set({ week: updatedWeek, isDirty: false, weekSaves: [] })
        const draft = buildWeekDraft({
          userId: useAuthentificationStore.getState().user?._id,
          route: getCurrentRoute(),
          scope: WEEK_DRAFT_SCOPE_GENERATOR,
          week: savedWeek,
        })
        await removeWeekDraft(draft?.key)
        return finishValidation({
          ok: true,
          dayKey,
          skippedCount,
          skippedDetails,
        })
      }

      return failValidation(saveResult?.data?.message || 'Échec de sauvegarde après validation')
    },

    validateWeekPair: async (options = {}) => {
      const week = get().week
      if (!week) return false

      const getRealDays = (weekToValidate) =>
        [...(weekToValidate?.days || [])]
          .filter((day) => day.isReal)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
      const realDays = getRealDays(week)
      const totalCount = realDays.length
      if (totalCount === 0) {
        toast.info('Aucun jour réel à valider')
        return false
      }

      const route = options?.route || getCurrentRoute()
      const buildRetry = ({ allowWithoutLocalDraft = options?.allowWithoutLocalDraft } = {}) => {
        return () => get().validateWeekPair({ route, allowWithoutLocalDraft })
      }
      const user = useAuthentificationStore.getState().user
      const initials = user?.initials || '??'
      const text = buildValidationCommentText(initials)

      set({ isDayValidationLoading: true })
      try {
      const { canSave, draft, retryAfterReauth, weekPayload } = await protectServerSave({
        week,
        route,
        scope: WEEK_DRAFT_SCOPE_GENERATOR,
        buildRetry,
        allowWithoutLocalDraft: options?.allowWithoutLocalDraft,
      })
      if (!canSave) {
        set({ isDayValidationLoading: false })
        return false
      }

      const users = useUsersStore.getState().users
      const rooms = useRoomsStore.getState().rooms
      const currentSaveResult = await saveWeekService(
        week._id,
        weekPayload,
        PROTECTED_SAVE_CONFIG
      )
      if (currentSaveResult?.status === 401) {
        set({ isDayValidationLoading: false })
        markSaveSessionExpired({
          draftKey: draft?.key,
          retry: retryAfterReauth,
          result: currentSaveResult,
        })
        return false
      }
      if (currentSaveResult?.status !== 200) {
        set({ isDayValidationLoading: false })
        toast.error(
          currentSaveResult?.data?.message ||
            "Échec de sauvegarde avant l'envoi des heures à Mon Espace"
        )
        return false
      }

      const savedWeek = currentSaveResult?.data?.updatedWeek
      if (!savedWeek) {
        set({ isDayValidationLoading: false })
        toast.error("Échec de récupération de la semaine sauvegardée avant l'envoi Mon Espace")
        return false
      }

      populateWeek(savedWeek, users, rooms)
      set({ week: savedWeek, isDirty: false, weekSaves: [] })
      await removeWeekDraft(draft?.key)

      const results = []
      for (const day of getRealDays(savedWeek)) {
        const dayKey = toDayKey(day.date)
        try {
          const hoursByUser = buildWorkedHoursPayload(savedWeek, dayKey)
          const result = await validateWorkedHoursService(dayKey, hoursByUser)
          const skippedCount = result?.data?.externalData?.skipped?.length || 0
          const skippedDetailsFromResponse = Array.isArray(result?.data?.skippedDetails)
            ? result.data.skippedDetails
            : []
          const skippedDetails =
            skippedDetailsFromResponse.length > 0
              ? skippedDetailsFromResponse
              : buildWorkedHoursSkippedDetails(result?.data?.externalData?.skipped)

          if (result?.status !== 200) {
            results.push({
              ok: false,
              dayKey,
              message: result?.data?.message || 'Échec de validation de la journée',
            })
            continue
          }

          if (skippedCount > 0) {
            addWorkedHoursSkippedInfo(dayKey, skippedCount, skippedDetails)
          }

          results.push({
            ok: true,
            dayKey,
            skippedCount,
            skippedDetails,
          })
        } catch (error) {
          results.push({
            ok: false,
            dayKey,
            message: error?.message || 'Échec de validation de la journée',
          })
        }
      }

      const successCount = results.filter((result) => result?.ok).length
      const failureCount = results.filter((result) => result && !result.ok).length
      const skippedCount = results.reduce(
        (count, result) => count + (result?.skippedCount || 0),
        0
      )
      const attemptedCount = results.length
      const successfulDayKeys = results
        .filter((result) => result?.ok)
        .map((result) => result.dayKey)

      if (successfulDayKeys.length > 0) {
        const nextWeek = JSON.parse(JSON.stringify(savedWeek))
        nextWeek.days
          .filter((day) => day.isReal && successfulDayKeys.includes(toDayKey(day.date)))
          .forEach((day) => setValidationCommentOnDay(day, text))

        unpopulateWeek(nextWeek)
        const saveResult = await saveWeekService(savedWeek._id, nextWeek)
        set({ isDayValidationLoading: false })

        if (saveResult?.status !== 200) {
          toast.error(saveResult?.data?.message || 'Échec de sauvegarde après validation semaine')
          return false
        }

        const updatedWeek = saveResult?.data?.updatedWeek
        populateWeek(updatedWeek, users, rooms)
        set({ week: updatedWeek, isDirty: false, weekSaves: [] })
      } else {
        set({ isDayValidationLoading: false })
      }

      if (failureCount > 0 || attemptedCount < totalCount) {
        const suffix =
          attemptedCount < totalCount
            ? `, arrêtée après ${attemptedCount}/${totalCount} jour(s)`
            : ''
        toast.warn(
          `Validation semaine terminée : ${successCount}/${totalCount} jour(s) validé(s), ${failureCount} échec(s)${suffix}.`
        )
        return false
      }

      if (skippedCount > 0) {
        toast.warn(
          `Validation semaine enregistrée : ${successCount} jour(s) validé(s), ${skippedCount} ligne(s) non importée(s). Détail dans Infos.`
        )
      } else {
        toast.success(`Validation semaine enregistrée : ${successCount} jour(s) validé(s).`)
      }

      return true
      } catch (error) {
        toast.error(error?.message || 'Échec de validation de la semaine')
        return false
      } finally {
        set({ isDayValidationLoading: false })
      }
    },

    // Save Indispos
    saveIndispos: async (options = {}) => {
      const week = get().week
      if (!week) return false

      const route = options?.route || getCurrentRoute()
      const buildRetry = ({ allowWithoutLocalDraft = options?.allowWithoutLocalDraft } = {}) => {
        return () => get().saveIndispos({ route, allowWithoutLocalDraft })
      }
      const { canSave, draft, retryAfterReauth, weekPayload } = await protectServerSave({
        week,
        route,
        scope: WEEK_DRAFT_SCOPE_INDISPO,
        buildRetry,
        allowWithoutLocalDraft: options?.allowWithoutLocalDraft,
      })
      if (!canSave) return false

      set({ isWeekSaving: true })
      const result = await saveIndispoService(week._id, weekPayload, PROTECTED_SAVE_CONFIG).finally(
        () => set({ isWeekSaving: false })
      )
      if (result?.status === 401) {
        markSaveSessionExpired({ draftKey: draft?.key, retry: retryAfterReauth, result })
        return false
      }
      if (result?.status === 200) {
        toast.success(result?.data?.message)
        set({ isDirty: false })
        await removeWeekDraft(draft?.key)
        return true
      }
      return false
    },

    // Toggle the editable field of the week
    toggleIsWeekEditable: async () => {
      const week = get().week
      set({ isWeekSaving: true })
      const result = await toggleIsWeekEditableService(week._id)
      set({ isWeekSaving: false })
      if (result?.status === 200) {
        toast.success(result?.data?.message)
        const updatedWeek = result?.data?.updatedWeek
        if (updatedWeek) {
          await useUsersStore.getState().fetchUsers(true)
          const users = useUsersStore.getState().users
          const rooms = useRoomsStore.getState().rooms
          populateWeek(updatedWeek, users, rooms)
          set({ week: updatedWeek })
        }
      }
    },

    // Toggle the editable field of the week
    toggleCanAskIndispo: async () => {
      const week = get().week
      set({ isWeekSaving: true })
      const result = await toggleCanAskIndispoService(week._id)
      set({ isWeekSaving: false })
      if (result?.status === 200) {
        toast.success(result?.data?.message)
        const updatedWeek = result?.data?.updatedWeek
        if (updatedWeek) {
          const users = useUsersStore.getState().users
          const rooms = useRoomsStore.getState().rooms
          populateWeek(updatedWeek, users, rooms)
          set({ week: updatedWeek })
        }
      }
    },

    // Toggle the isVisibleByMG field of the week
    toggleVisibleMG: async () => {
      const week = get().week
      const setWeekValue = get().setWeekValue
      const result = await toggleVisibleMGService(week._id)
      if (result?.status === 200) {
        toast.success(result?.data?.message)
        setWeekValue('isVisibleByMG', result?.data?.nextIsVisible)
      }
    },

    // Toggle the realEnabled of one day
    toggleRealEnabled: async (day) => {
      const updateDayValue = get().updateDayValue
      const result = await toggleRealEnabledService(day._id)
      if (result?.status === 200) {
        toast.success(result?.data?.message)
        updateDayValue(result?.data?.dayId, 'realEnabled', result?.data?.nextRealEnabled)
      }
    },
    setWeek: (nextWeek) =>
      set((state) => {
        state.week = nextWeek
      }),
    restoreWeekDraft: (draftWeek) => {
      const users = useUsersStore.getState().users
      const rooms = useRoomsStore.getState().rooms
      const nextWeek = JSON.parse(JSON.stringify(draftWeek))
      populateWeek(nextWeek, users, rooms)
      set({ week: nextWeek, isDirty: true, weekSaves: [] })
    },
    setWeekValue: (key, value) =>
      set((state) => {
        state.week[key] = value
      }),
    setWeekValueByPlace: (key, value, placeId) =>
      set((state) => {
        if (state.week[key] === undefined) {
          state.week[key] = { 1: 0, 2: 0 }
        }
        state.week[key][placeId] = value
      }),
    setVersatileModifier: (value) =>
      set((state) => {
        state.week.versatilesModifier = value
      }),
    updateDayValue: (dayId, key, value) =>
      set((state) => {
        const dayToUpdate = state.week.days.find((d) => d._id === dayId)
        if (dayToUpdate) {
          dayToUpdate[key] = value
          state.isDirty = true
        }
      }),
    updateDayMorningNeedsMg: (dayId, placeId, value) =>
      set((state) => {
        const dayToUpdate = state.week.days.find((d) => d._id === dayId)
        if (dayToUpdate) {
          if (dayToUpdate['morningNeedsMgByPlace'] === undefined) {
            dayToUpdate['morningNeedsMgByPlace'] = { 1: 0, 2: 0 }
          }
          dayToUpdate['morningNeedsMgByPlace'][placeId] = value
          state.isDirty = true
        }
      }),
    updateUserWeekValue: (userWeekId, key, value) =>
      set((state) => {
        const userWeekToUpdate = state.week.users_week.find((uw) => uw._id === userWeekId)
        if (userWeekToUpdate) {
          userWeekToUpdate[key] = value
          state.isDirty = true
        }
      }),
    updateUserIntervalValue: (userIntervalId, key, value) =>
      set((state) => {
        const userIntervalToUpdate = state.week.days
          .flatMap((day) => day.intervals)
          .flatMap((interval) => interval.users_interval)
          .find((ui) => ui._id === userIntervalId)
        if (userIntervalToUpdate) {
          if (userIntervalToUpdate[key] !== value) {
            recordForUndo(state, get().week)
            userIntervalToUpdate[key] = value
            state.isDirty = true
          }
        }
      }),
    updateSessionValue: (sessionId, key, value) =>
      set((state) => {
        const sessionToUpdate = state.week.days
          .flatMap((day) => day.intervals)
          .flatMap((interval) => interval.sessions)
          .find((session) => session._id === sessionId)
        if (sessionToUpdate) {
          if (sessionToUpdate[key] !== value) {
            recordForUndo(state, get().week)
            sessionToUpdate[key] = value
            state.isDirty = true
          }
        }
      }),
    refreshSession: (sessionId, nextSession) =>
      set((state) => {
        const sessionToUpdate = state.week?.days
          ?.flatMap((day) => day.intervals)
          ?.flatMap((interval) => interval.sessions)
          ?.find((session) => session._id === sessionId)
        if (sessionToUpdate) {
          Object.assign(sessionToUpdate, nextSession)
        }
      }),

    // Update all session in selection to the desired value
    updateAllSessionValueSelected: (key, value) =>
      set((state) => {
        recordForUndo(state, get().week)
        const selectedIds = Object.keys(useSelectionStore.getState().selectedSessions)
        const allSessionToUpdate = state.week.days
          .flatMap((day) => day.intervals)
          .flatMap((interval) => interval.sessions)
          .filter((session) => selectedIds.includes(session._id))
        allSessionToUpdate.forEach((s) => {
          s[key] = value
        })
        state.isDirty = true
      }),
    setEventSession: (sessionId, event) =>
      set((state) => {
        recordForUndo(state, get().week)
        const sessionToUpdate = state.week.days
          .flatMap((day) => day.intervals)
          .flatMap((interval) => interval.sessions)
          .find((session) => session._id === sessionId)
        if (sessionToUpdate) {
          let nextEvents = [...sessionToUpdate.events]
          if (!nextEvents.includes(event)) {
            nextEvents.push(event)
          }
          sessionToUpdate.events = nextEvents
          state.isDirty = true
        }
      }),
    clearSessionDecorations: (sessionId) =>
      set((state) => {
        const sessionToClear = state.week.days
          .flatMap((day) => day.intervals)
          .flatMap((interval) => interval.sessions)
          .find((session) => session._id === sessionId)
        const hasDecorations =
          !!sessionToClear?.highlight || (sessionToClear?.events?.length || 0) > 0
        if (hasDecorations) {
          recordForUndo(state, get().week)
          sessionToClear.highlight = false
          sessionToClear.events = []
          state.isDirty = true
        }
      }),

    // Reset all sessions field (mg, events, highlight,... )
    resetSession: (sessionId) =>
      set((state) => {
        const sessionToReset = state.week.days
          .flatMap((day) => day.intervals)
          .flatMap((interval) => interval.sessions)
          .find((session) => session._id === sessionId)
        if (sessionToReset) {
          recordForUndo(state, get().week)
          sessionToReset.mg = null
          sessionToReset.lock = false
          sessionToReset.events = []
          sessionToReset.highlight = false
          sessionToReset.duration = SESSION_DEFAULT_URATION
          sessionToReset.available = true
          state.isDirty = true
        }
      }),

    // Same as reset but for all selected sessions
    resetAllSelectedSession: () =>
      set((state) => {
        recordForUndo(state, get().week)
        const selectedIds = Object.keys(useSelectionStore.getState().selectedSessions)
        const allSessionToUpdate = state.week.days
          .flatMap((day) => day.intervals)
          .flatMap((interval) => interval.sessions)
          .filter((session) => selectedIds.includes(session._id))
        allSessionToUpdate.forEach((s) => {
          s.mg = null
          s.lock = false
          s.events = []
          s.highlight = false
          s.duration = SESSION_DEFAULT_URATION
          s.available = true
        })
        state.isDirty = true
      }),
    addAdditionalTimeForUser: (
      dayIndex,
      user,
      commentary = '',
      duration = 0,
      lastID,
      lastTimeStamp
    ) =>
      set((state) => {
        recordForUndo(state, get().week)
        const day = state.week.days
          .filter((d) => d.isReal)
          .find((d) => {
            const dayIndexLocal = dayjs(d.date).day() === 0 ? 6 : dayjs(d.date).day() - 1
            return dayIndexLocal === dayIndex
          })
        if (day) {
          const d = new Date()
          day.additionalTimes.push({
            _id: lastID,
            commentary,
            duration,
            user,
            dayIndex,
            timeStamp: lastTimeStamp || d.getTime().toString(),
          })
          state.isDirty = true
        }
      }),
    deleteAT: (searchedId) =>
      set((state) => {
        recordForUndo(state, get().week)
        state.week.days
          .filter((d) => d.isReal)
          .forEach((d) => {
            d.additionalTimes = d.additionalTimes.filter(
              (at) => at.timeStamp !== searchedId && at?._id !== searchedId
            )
          })
        state.isDirty = true
      }),
    editAT: (searchedId, key, value) =>
      set((state) => {
        recordForUndo(state, get().week)
        const toEdit = state.week.days
          .flatMap((d) => d.additionalTimes)
          .find((at) => at.timeStamp === searchedId || at?._id === searchedId)

        if (toEdit) {
          toEdit[key] = value
          state.isDirty = true
        }
      }),

    // When pasting some data it will fill up the sessions according to the startSelectingPosition
    onPasteWeekValues: (pastedText) =>
      set((state) => {
        recordForUndo(state, get().week)
        const users = useUsersStore.getState().users
        const { startSelectingPos, isSelectingReal } = useSelectionStore.getState()
        if (startSelectingPos) {
          const lines = pastedText.replace(/\r/g, '').split(`\n`)
          const values = []
          for (var i = 0; i < lines.length; i++) {
            var elements = lines[i].split('\t')
            values.push(elements)
          }
          const width = values[0].length
          const height = values.length
          const startX = startSelectingPos.x
          const startY = startSelectingPos.y
          state.week.days
            .filter((d) => d.isReal === isSelectingReal)
            .flatMap((day) => day.intervals)
            .flatMap((interval) => interval.sessions)
            .forEach((s) => {
              if (s.x >= startX && s.x < startX + width) {
                if (s.y >= startY && s.y < startY + height) {
                  const currentX = s.x - startX
                  const currentY = s.y - startY
                  const userToSet = values[currentY][currentX]
                  const foundUser = Object.values(users)
                    .filter((u) => u.state !== 0)
                    .find((u) => u.initials === userToSet)
                  if (foundUser) {
                    s.mg = foundUser?._id
                  } else {
                    s.mg = null
                  }
                }
              }
            })
          state.isDirty = true
        }
      }),
    addDayComment: (dayIndex, isReal, place_id) => {
      const d = new Date()
      const tempId = d.getTime().toString()
      set((state) => {
        recordForUndo(state, get().week)
        const day = state.week.days
          .filter((d) => d.isReal === isReal)
          .find((d) => {
            const dayIndexLocal = dayjs(d.date).day() === 0 ? 6 : dayjs(d.date).day() - 1
            return dayIndexLocal === dayIndex
          })
        day.commentaries.push({
          text: '',
          place_id,
          timeStamp: tempId,
        })
        state.isDirty = true
      })
      return tempId
    },
    deleteDayComment: (id, isReal) =>
      set((state) => {
        recordForUndo(state, get().week)
        state.week.days
          .filter((d) => d.isReal === isReal)
          .forEach((d) => {
            d.commentaries = d.commentaries.filter((c) => c.timeStamp !== id && c?._id !== id)
          })
        state.isDirty = true
      }),
    editDayComment: (id, key, value) =>
      set((state) => {
        recordForUndo(state, get().week)
        const toEdit = state.week.days
          .flatMap((d) => d.commentaries)
          .find((c) => c.timeStamp === id || c?._id === id)
        if (toEdit) {
          toEdit[key] = value
          state.isDirty = true
        }
      }),

    // Migrate only 1 day
    prevToRealOneDay: (dayDate) =>
      set((state) => {
        recordForUndo(state, get().week)
        const days = state.week.days.filter((d) => d.date === dayDate)
        const prev = days.filter((d) => !d.isReal)[0]
        const real = days.filter((d) => d.isReal)[0]
        real.commentaries = deepCloneButKeepId(prev.commentaries, real.commentaries)
        real.intervals = deepCloneButKeepId(prev.intervals, real.intervals)
        state.isDirty = true
      }),

    // Refresh local data of indispo for a user (after a chat indispo validation)
    updateIndispoOfUser: (userId, propal) =>
      set((state) => {
        state.week.days
          .filter((d) => !d.isReal)
          .forEach((day, dayIndex) => {
            day.intervals.forEach((interval, intervalIndex) => {
              const ui = interval.users_interval.find((ui) => ui?.user?._id.toString() === userId)
              if (ui) {
                ui.available = propal[dayIndex][intervalIndex]
              }
            })
          })
        state.week.users_week.forEach((uw) => {
          if (uw.user._id.toString() === userId) {
            uw.indispoChecked = true
          }
        })
      }),

    undo: () => {
      set((state) => {
        if (state.weekSaves.length > 0) {
          state.week = state.weekSaves.pop()
        } else {
          toast.info('Pas de sauvegarde précédente')
        }
      })
    },
  }))
)

export const WeekStoreWrapper = ({ children }) => {
  const { pathname } = useLocation()

  const { fetchWeek, fetchWeekForMG } = useWeekStore(
    useShallow((state) => ({
      fetchWeek: state.fetchWeek,
      fetchWeekForMG: state.fetchWeekForMG,
    }))
  )
  const { date } = useDateStore(
    useShallow((state) => ({
      date: state.date,
    }))
  )
  const users = useUsersStore(useShallow((state) => state.users))
  const rooms = useRoomsStore(useShallow((state) => state.rooms))

  const debouncedDate = useDebounce(date, 500)

  useEffect(() => {
    if (debouncedDate) {
      if (pathname === ROUTE_WEEK_VIEW) {
        fetchWeekForMG()
      } else {
        fetchWeek()
      }
    }
  }, [debouncedDate, fetchWeek, users, rooms, pathname, fetchWeekForMG])

  return children
}
